/**
 * Singleton holding the live DOM anchor for the photo drawer.
 *
 * `EditorView` renders an empty `<div>` inside its workspace panel and
 * passes `setPhotoDrawerHost` as the React `ref` callback. The desktop
 * `PhotoDrawer` portals itself into that element, and the editor's
 * click-outside / `selection:cleared` guards use it to recognise clicks
 * that originated inside the drawer.
 */
let host: HTMLElement | null = null;

/**
 * React `ref` callback that should be attached to the host `<div>` inside
 * `EditorView`. React calls it with the element on mount and with `null`
 * on unmount, so the value here always matches the live DOM.
 */
export const setPhotoDrawerHost = (element: HTMLElement | null): void => {
  host = element;
};

/** Returns the currently mounted host element, or `null` if none is mounted. */
export const getPhotoDrawerHost = (): HTMLElement | null => host;

/**
 * Convenience helper used by click-outside / `selection:cleared` guards
 * to recognise interactions that happened inside the drawer (and should
 * therefore not deselect the active canvas object).
 */
export const isInsidePhotoDrawer = (target: EventTarget | Element | null): boolean => {
  if (!host || !target || !(target instanceof Element)) {
    return false;
  }
  return host.contains(target);
};

/**
 * Singleton holding the drawer's animated close handler (`beginCloseDrawer`).
 *
 * `PhotoDrawer` registers it while mounted so the editor's single
 * click-outside dispatcher can dismiss the drawer *with* its slide-out
 * animation, instead of hard-dispatching `setPhotoDrawerClosed` (which would
 * make the drawer disappear instantly).
 */
let closeHandler: (() => void) | null = null;

/** Registers (or clears with `null`) the drawer's animated close handler. */
export const setPhotoDrawerCloseHandler = (handler: (() => void) | null): void => {
  closeHandler = handler;
};

/**
 * Triggers the registered animated close. Returns `true` when a handler was
 * present (and invoked) so callers can fall back to a plain dispatch when the
 * drawer is not currently mounted.
 */
export const requestPhotoDrawerClose = (): boolean => {
  if (closeHandler) {
    closeHandler();
    return true;
  }
  return false;
};
